import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { GoogleMap, Marker, InfoWindow, DirectionsRenderer, useLoadScript } from '@react-google-maps/api';
import styled from 'styled-components';

// Static libraries array to prevent performance warnings
const GOOGLE_MAPS_LIBRARIES: ("places" | "geometry")[] = ['places', 'geometry'];

const MapContainer = styled.div`
  margin-bottom: 20px;
  position: relative;
`;

const InstructionsPanel = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px 4px 0 0;
  padding: 10px;
  margin-bottom: 0;
  font-size: 14px;
`;

const MapWrapper = styled.div<{ $height?: number }>`
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 4px 4px;
  height: ${props => props.$height != null ? `${props.$height}px` : '400px'};
  max-height: ${props => props.$height != null ? `${props.$height}px` : '400px'};
  min-height: 0;
  position: relative;
  /* Clip map panes / hit targets so they cannot overlap the route list below */
  overflow: hidden;
  isolation: isolate;
  
  /* Ensure Google Maps controls are not blocked */
  .gm-style .gm-style-cc,
  .gm-style .gm-style-mtc,
  .gm-style .gmnoprint {
    z-index: 1000 !important;
  }
  
  /* Ensure zoom controls are clickable and visible */
  .gm-style .gmnoprint div {
    pointer-events: auto !important;
  }
  
  /* Specifically target zoom controls */
  .gm-style .gm-style-cc button,
  .gm-style .gmnoprint button {
    pointer-events: auto !important;
    cursor: pointer !important;
    opacity: 1 !important;
    visibility: visible !important;
  }
  
  /* Ensure the zoom control container is visible */
  .gm-style .gmnoprint {
    display: block !important;
    visibility: visible !important;
  }
`;

const MapTypeButtons = styled.div`
  position: absolute;
  bottom: 10px;
  left: 10px;
  z-index: 100; /* Lower than Google Maps controls */
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  display: flex;
  gap: 1px;
  overflow: hidden;
`;

const MapTypeButton = styled.button<{ active?: boolean }>`
  border: none;
  padding: 8px 12px;
  background: ${props => props.active ? '#007bff' : 'white'};
  color: ${props => props.active ? 'white' : '#212529'};
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#0056b3' : '#f8f9fa'};
  }
`;

const LocationButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 100;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AddStopButton = styled(LocationButton)<{ $active?: boolean }>`
  right: auto;
  left: 10px;
  background: ${props => props.$active ? '#007bff' : 'white'};
  color: ${props => props.$active ? 'white' : '#212529'};
  border-color: ${props => props.$active ? '#007bff' : '#dee2e6'};

  &:hover {
    background: ${props => props.$active ? '#0056b3' : '#f8f9fa'};
  }
`;

const LocationDropdown = styled.div`
  position: absolute;
  top: 50px;
  right: 10px;
  z-index: 100;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const RouteSelector = styled.div`
border: 1px solid #dee2e6;
border-top: none;
border-radius: 0 0 4px 4px;
background: white;
overflow: hidden;
position: relative;
z-index: 20;
pointer-events: auto;
`;

const RouteSelectorHeader = styled.div`
padding: 8px 12px;
background: #f8f9fa;
border-bottom: 1px solid #dee2e6;
font-size: 13px;
font-weight: 500;
color: #495057;
`;

const RouteOption = styled.button<{ $selected?: boolean }>`
display: flex;
align-items: center;
width: 100%;
padding: 10px 12px;
border: none;
border-bottom: 1px solid #f0f0f0;
background: ${props => props.$selected ? '#e8f0fe' : 'white'};
cursor: pointer;
text-align: left;
transition: background 0.15s;
gap: 10px;
position: relative;
z-index: 1;
pointer-events: auto;

&:hover {
  background: ${props => props.$selected ? '#d2e3fc' : '#f8f9fa'};
}

&:last-child {
  border-bottom: none;
}
`;

const RouteNumber = styled.span<{ $selected?: boolean }>`
width: 24px;
height: 24px;
border-radius: 50%;
background: ${props => props.$selected ? '#0d47a1' : '#6c757d'};
color: white;
font-size: 12px;
font-weight: 600;
display: flex;
align-items: center;
justify-content: center;
flex-shrink: 0;
`;

const RouteSummaryText = styled.span`
flex: 1;
font-size: 13px;
color: #495057;
text-align: left;
`;

const RouteStats = styled.span`
font-size: 13px;
color: #212529;
font-weight: 500;
white-space: nowrap;
`;

const LocationOption = styled.button`
  border: none;
  background: white;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  border-bottom: 1px solid #f8f9fa;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StopList = styled.div`
  border: 1px solid #dee2e6;
  border-top: none;
  background: white;
`;

const StopListHeader = styled.div`
  padding: 8px 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  font-size: 13px;
  font-weight: 500;
  color: #495057;
`;

const StopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
  color: #495057;

  &:last-child {
    border-bottom: none;
  }
`;

const StopRemoveButton = styled.button`
  margin-left: auto;
  border: none;
  background: transparent;
  color: #dc3545;
  cursor: pointer;
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 4px;

  &:hover {
    background: #f8d7da;
  }
`;

interface RouteStop {
  id: string;
  lat: number;
  lng: number;
}

const MAX_ROUTE_STOPS = 5;

function getRouteTotals(route: google.maps.DirectionsRoute): { distanceKm: number; durationMin: number } | null {
  let distanceM = 0;
  let durationS = 0;
  for (const leg of route.legs) {
    if (!leg.distance || !leg.duration) return null;
    distanceM += leg.distance.value;
    durationS += leg.duration.value;
  }
  return {
    distanceKm: Math.round(distanceM / 10) / 100,
    durationMin: Math.round(durationS / 60)
  };
}

function buildRouteCacheKey(
  origin: { lat: number; lng: number },
  destination: { lat: number; lng: number },
  stops: RouteStop[]
): string {
  const stopKey = stops
    .map(s => `${s.lat.toFixed(6)},${s.lng.toFixed(6)}`)
    .join('|');
  return `${origin.lat.toFixed(6)},${origin.lng.toFixed(6)}_${destination.lat.toFixed(6)},${destination.lng.toFixed(6)}_${stopKey}`;
}

interface GPSMapProps {
  startLocation?: { lat: number; lng: number };
  endLocation?: { lat: number; lng: number };
  onLocationSelect: (lat: number, lng: number, type: 'start' | 'end') => void;
  locationType: 'start' | 'end';
  isPointActivity?: boolean;
  autoFitBounds?: boolean; // New prop to control automatic bounds fitting
  readOnly?: boolean; // When true, map is read-only (no editing allowed)
  hideInstructions?: boolean; // When true, hide the instructions panel (e.g. for compact embedded use)
  height?: number; // Custom map height in px (e.g. for compact embedded use); default 400
  onRouteSelect?: (distanceKm: number, durationMin: number) => void;
}

// BC coordinates
const BC_CENTER = {
  lat: 53.7267,
  lng: -127.6476
};

// Custom marker icons
const createMarkerIcon = (type: 'start' | 'end'): google.maps.Symbol => {
  const color = type === 'start' ? '#28a745' : '#dc3545';
  return {
    path: google.maps.SymbolPath.CIRCLE,
    scale: 10,
    fillColor: color,
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 2
  };
};

const createStopMarkerIcon = (): google.maps.Symbol => ({
  path: google.maps.SymbolPath.CIRCLE,
  scale: 8,
  fillColor: '#007bff',
  fillOpacity: 1,
  strokeColor: '#ffffff',
  strokeWeight: 2
});

const GoogleGPSMap: React.FC<GPSMapProps> = ({
  startLocation,
  endLocation,
  onLocationSelect,
  locationType,
  isPointActivity = false,
  autoFitBounds = true,
  readOnly = false,
  hideInstructions = false,
  height,
  onRouteSelect
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isIpad, setIsIpad] = useState(false);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [infoWindowPosition, setInfoWindowPosition] = useState<google.maps.LatLng | null>(null);
  const [mapType, setMapType] = useState<'roadmap' | 'hybrid'>('roadmap');
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [mapCenter, setMapCenter] = useState(() => {
    // Initialize center based on existing markers
    if (startLocation) return startLocation;
    if (endLocation) return endLocation;
    return BC_CENTER;
  });
  const [mapZoom, setMapZoom] = useState(() => {
    // Initialize zoom based on whether we have markers
    if (startLocation || endLocation) return 12;
    return 7;
  });
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [directionsResult, setDirectionsResult] = useState<google.maps.DirectionsResult | null>(null);
  const [directionsLoading, setDirectionsLoading] = useState(false);
  const [routeStops, setRouteStops] = useState<RouteStop[]>([]);
  const [addStopMode, setAddStopMode] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const directionsServiceRef = useRef<google.maps.DirectionsService | null>(null);
  const activeMarkerRef = useRef<'start' | 'end'>(locationType);
  const routeCacheRef = useRef<Map<string, google.maps.DirectionsResult>>(new Map());
  const routeRequestTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onRouteSelectRef = useRef(onRouteSelect);

  useEffect(() => {
    onRouteSelectRef.current = onRouteSelect;
  }, [onRouteSelect]);

  // Parents often pass `{ lat, lng }` literals that get a new reference every render. Effects
  // must depend on coordinate *values*, not object identity — otherwise alternate-route
  // selection is immediately wiped by directions cache/load logic calling setSelectedRouteIndex(0).
  const startLat = startLocation?.lat;
  const startLng = startLocation?.lng;
  const endLat = endLocation?.lat;
  const endLng = endLocation?.lng;
  const hasStartCoords = startLat != null && startLng != null && startLocation != null;
  const hasEndCoords = endLat != null && endLng != null && endLocation != null;
  const routeStopsKey = routeStops.map(s => `${s.lat.toFixed(6)},${s.lng.toFixed(6)}`).join('|');

  const notifyRouteSelect = (route: google.maps.DirectionsRoute) => {
    const cb = onRouteSelectRef.current;
    if (!cb) return;
    const totals = getRouteTotals(route);
    if (totals) {
      cb(totals.distanceKm, totals.durationMin);
    }
  };

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  // Memoize map options - keep them simple and stable
  const mapOptions = useMemo<google.maps.MapOptions>(() => ({
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
    zoomControl: true,
    mapTypeId: mapType,
    gestureHandling: 'greedy',
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      }
    ]
  }), [mapType]); // Only depend on mapType

  // Handle getting current location
  const handleGetCurrentLocation = useCallback((locationType?: 'start' | 'end') => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }

    setIsGettingLocation(true);
    setShowLocationDropdown(false);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        
        // Ensure exactly 8 decimal places
        const lat = Number(latitude.toFixed(8));
        const lng = Number(longitude.toFixed(8));
        
        if (isPointActivity) {
          // For point activities, set both start and end to the same location
          onLocationSelect(lat, lng, 'start');
          onLocationSelect(lat, lng, 'end');
        } else {
          // Use the specified location type or the current active marker type
          const targetType = locationType || activeMarkerRef.current;
          onLocationSelect(lat, lng, targetType);
        }
        
        // Center the map on the new location
        if (mapRef.current) {
          mapRef.current.setCenter({ lat, lng });
          mapRef.current.setZoom(15); // Zoom in closer for current location
        }
        
        setIsGettingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        let errorMessage = 'Unable to get your current location.';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location services in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
        }
        
        alert(errorMessage);
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  }, [onLocationSelect, isPointActivity]);

  const addRouteStopAt = useCallback((lat: number, lng: number) => {
    if (routeStops.length >= MAX_ROUTE_STOPS) {
      alert(`Maximum of ${MAX_ROUTE_STOPS} route stops allowed.`);
      return;
    }
    setRouteStops(prev => [
      ...prev,
      { id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, lat, lng }
    ]);
    setAddStopMode(false);
  }, [routeStops.length]);

  // Memoize event handlers
  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (readOnly) return; // Don't handle clicks in read-only mode
    if (!e.latLng) return;

    if (addStopMode && !isPointActivity) {
      addRouteStopAt(e.latLng.lat(), e.latLng.lng());
      return;
    }
    
    if (isMobile || isIpad) {
      if (isPointActivity) {
        // For point activities, set both start and end to the same location
        onLocationSelect(e.latLng.lat(), e.latLng.lng(), 'start');
        onLocationSelect(e.latLng.lat(), e.latLng.lng(), 'end');
      } else {
        onLocationSelect(e.latLng.lat(), e.latLng.lng(), activeMarkerRef.current);
      }
    }
  }, [readOnly, addStopMode, isPointActivity, addRouteStopAt, isMobile, isIpad, onLocationSelect]);

  const handleMapRightClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (readOnly) return; // Don't handle right-clicks in read-only mode
    if (!e.latLng) return;
    
    if (!isMobile && !isIpad) {
      setInfoWindowPosition(e.latLng);
      setShowInfoWindow(true);
    }
  }, [readOnly, isMobile, isIpad]);

  const handleMarkerDragEnd = useCallback((e: google.maps.MapMouseEvent, type: 'start' | 'end') => {
    if (readOnly) return; // Don't handle drag in read-only mode
    if (!e.latLng) return;
    if (isPointActivity) {
      // For point activities, set both start and end to the same location
      onLocationSelect(e.latLng.lat(), e.latLng.lng(), 'start');
      onLocationSelect(e.latLng.lat(), e.latLng.lng(), 'end');
    } else {
      onLocationSelect(e.latLng.lat(), e.latLng.lng(), type);
    }
  }, [readOnly, onLocationSelect, isPointActivity]);

  const handleContextMenuSelect = useCallback((type: 'start' | 'end' | 'stop') => {
    if (!infoWindowPosition) return;
    const lat = infoWindowPosition.lat();
    const lng = infoWindowPosition.lng();
    if (type === 'stop') {
      addRouteStopAt(lat, lng);
    } else if (isPointActivity) {
      onLocationSelect(lat, lng, 'start');
      onLocationSelect(lat, lng, 'end');
    } else {
      onLocationSelect(lat, lng, type);
    }
    setShowInfoWindow(false);
  }, [infoWindowPosition, onLocationSelect, isPointActivity, addRouteStopAt, routeStops.length]);

  const handleStopDragEnd = useCallback((stopId: string, e: google.maps.MapMouseEvent) => {
    if (readOnly || !e.latLng) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setRouteStops(prev => prev.map(stop => (stop.id === stopId ? { ...stop, lat, lng } : stop)));
  }, [readOnly]);

  const handleRemoveStop = useCallback((stopId: string) => {
    setRouteStops(prev => prev.filter(stop => stop.id !== stopId));
  }, []);

  const handleMapTypeChange = useCallback((newMapType: 'roadmap' | 'hybrid') => {
    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      const zoom = mapRef.current.getZoom();
      if (center) {
        setMapCenter({ lat: center.lat(), lng: center.lng() });
      }
      if (zoom) {
        setMapZoom(zoom);
      }
    }
    setMapType(newMapType);
  }, []);

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    
    // Initialize Directions Service
    directionsServiceRef.current = new google.maps.DirectionsService();
    
    // Set initial center and zoom
    map.setCenter(mapCenter);
    map.setZoom(mapZoom);
    
    // Force zoom controls to be enabled with a delay to ensure map is fully loaded
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.setOptions({
          zoomControl: true,
          zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_CENTER
          }
        });
        
        // Force a map resize to ensure controls are properly rendered
        google.maps.event.trigger(mapRef.current, 'resize');
      }
    }, 500);
    
    setIsMapLoaded(true);
  }, [mapCenter, mapZoom]);

  const handleMapIdle = useCallback(() => {
    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      const zoom = mapRef.current.getZoom();
      if (center) {
        setMapCenter({ lat: center.lat(), lng: center.lng() });
      }
      if (zoom) {
        setMapZoom(zoom);
      }
    }
  }, []);

  // Function to calculate bounds and fit map to markers
  const fitMapToMarkers = useCallback(() => {
    if (!mapRef.current || !autoFitBounds) return; // Skip if autoFitBounds is false

    const bounds = new google.maps.LatLngBounds();
    let hasMarkers = false;

    if (hasStartCoords && startLat != null && startLng != null) {
      bounds.extend(new google.maps.LatLng(startLat, startLng));
      hasMarkers = true;
    }

    if (!isPointActivity && hasEndCoords && endLat != null && endLng != null) {
      bounds.extend(new google.maps.LatLng(endLat, endLng));
      hasMarkers = true;
    }

    routeStops.forEach(stop => {
      bounds.extend(new google.maps.LatLng(stop.lat, stop.lng));
      hasMarkers = true;
    });

    if (hasMarkers) {
      // If we have markers, fit the map to show them
      if (isPointActivity || (hasStartCoords && !hasEndCoords) || (!hasStartCoords && hasEndCoords)) {
        // For single point, center on it with a reasonable zoom
        const lat = hasStartCoords ? startLat! : endLat!;
        const lng = hasStartCoords ? startLng! : endLng!;
        mapRef.current.setCenter({ lat, lng });
        mapRef.current.setZoom(12); // Good zoom level for single location
      } else if (hasStartCoords && hasEndCoords) {
        // For two points, fit bounds with padding
        mapRef.current.fitBounds(bounds, {
          top: 50,
          right: 50,
          bottom: 50,
          left: 50
        });

        // Ensure minimum zoom level for very close points
        setTimeout(() => {
          const zoom = mapRef.current?.getZoom();
          if (zoom && zoom > 15) {
            mapRef.current?.setZoom(15);
          }
        }, 100);
      }
    }
  }, [startLat, startLng, endLat, endLng, hasStartCoords, hasEndCoords, isPointActivity, autoFitBounds, routeStops]);

  // Effect to calculate and display route when both start and end locations are available
  useEffect(() => {
    if (!isMapLoaded || !directionsServiceRef.current || isPointActivity) return;
    if (!hasStartCoords || !hasEndCoords || startLat == null || startLng == null || endLat == null || endLng == null) {
      setDirectionsResult(null);
      return;
    }

    const origin = { lat: startLat, lng: startLng };
    const destination = { lat: endLat, lng: endLng };

    const cacheKey = buildRouteCacheKey(origin, destination, routeStops);

    // Check cache first
    const cachedResult = routeCacheRef.current.get(cacheKey);
    if (cachedResult) {
      setDirectionsResult(cachedResult);
      setSelectedRouteIndex(0);
      const route = cachedResult.routes[0];
      if (route) {
        notifyRouteSelect(route);
      }
      return;
    }

    // Clear any pending timeout
    if (routeRequestTimeoutRef.current) {
      clearTimeout(routeRequestTimeoutRef.current);
    }

    // Debounce requests when markers are being dragged (only if not read-only)
    const delay = readOnly ? 0 : 500; // No delay for read-only, 500ms delay for editing

    routeRequestTimeoutRef.current = setTimeout(() => {
      setDirectionsLoading(true);

      const request: google.maps.DirectionsRequest = {
        origin,
        destination,
        travelMode: google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: routeStops.length === 0,
        ...(routeStops.length > 0 && {
          waypoints: routeStops.map(stop => ({
            location: { lat: stop.lat, lng: stop.lng },
            stopover: true
          }))
        })
      };

      directionsServiceRef.current!.route(request, (result, status) => {
        setDirectionsLoading(false);
        if (status === google.maps.DirectionsStatus.OK && result) {
          // Cache the result
          routeCacheRef.current.set(cacheKey, result);
          // Limit cache size to prevent memory issues (keep last 50 routes)
          if (routeCacheRef.current.size > 50) {
            const firstKey = routeCacheRef.current.keys().next().value;
            if (firstKey) {
              routeCacheRef.current.delete(firstKey);
            }
          }
          
          setDirectionsResult(result);
          setSelectedRouteIndex(0);
          const route = result.routes[0];
          if (route) {
            notifyRouteSelect(route);
          }
        } else {
          console.error('Directions request failed:', status);
          setDirectionsResult(null);
        }
      });
    }, delay);

    // Cleanup timeout on unmount or dependency change
    return () => {
      if (routeRequestTimeoutRef.current) {
        clearTimeout(routeRequestTimeoutRef.current);
      }
    };
  }, [
    isMapLoaded,
    hasStartCoords,
    hasEndCoords,
    startLat,
    startLng,
    endLat,
    endLng,
    isPointActivity,
    readOnly,
    routeStopsKey
  ]);

  // Effect to adjust map view when markers change or map loads
  useEffect(() => {
    if (isMapLoaded && mapRef.current && (hasStartCoords || hasEndCoords) && !directionsResult) {
      // Small delay to ensure map is fully loaded
      setTimeout(() => {
        fitMapToMarkers();
      }, 100);
    }
  }, [isMapLoaded, hasStartCoords, hasEndCoords, fitMapToMarkers, directionsResult]);

  // Check if device is mobile/tablet
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent;
      const isMobileDevice = /iPhone|Android/i.test(userAgent);
      const isIpadDevice = 
        /iPad/i.test(userAgent) || 
        (navigator.maxTouchPoints > 1 && /MacIntel/.test(navigator.platform)) ||
        (window.innerWidth <= 1180 && window.innerHeight <= 820 && navigator.maxTouchPoints > 1);
      
      setIsMobile(isMobileDevice);
      setIsIpad(isIpadDevice);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    window.addEventListener('orientationchange', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('orientationchange', checkMobile);
    };
  }, []);

  // Update active marker reference when locationType changes
  useEffect(() => {
    activeMarkerRef.current = locationType;
  }, [locationType]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showLocationDropdown && !target.closest('[data-location-dropdown]')) {
        setShowLocationDropdown(false);
      }
    };

    if (showLocationDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLocationDropdown]);

  if (loadError) {
    return (
      <MapContainer>
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          backgroundColor: '#f8d7da', 
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px'
        }}>
          <strong>Error loading Google Maps:</strong> {loadError.message}
          <br />
          <small>Please check your internet connection and Google Maps API key.</small>
        </div>
      </MapContainer>
    );
  }

  if (!isLoaded) {
    return (
      <MapContainer>
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          backgroundColor: '#d1ecf1', 
          color: '#0c5460',
          border: '1px solid #bee5eb',
          borderRadius: '4px'
        }}>
          <div>Loading Google Maps...</div>
          <small>This may take a few moments</small>
        </div>
      </MapContainer>
    );
  }

  const renderInstructions = () => {
    if (readOnly) {
      return (
        <InstructionsPanel>
          <strong>Read-Only View:</strong> GPS locations are displayed for reference. Map interactions are disabled.
        </InstructionsPanel>
      );
    }

    if (isPointActivity) {
      return (
        <InstructionsPanel>
          <strong>Instructions:</strong> Click on the map to set the location. You can also drag the marker to adjust its position.
        </InstructionsPanel>
      );
    }

    if (isIpad) {
      return (
        <InstructionsPanel>
          <strong>iPad Instructions:</strong> Tap and hold on the map to place the {locationType === 'start' ? 'start' : 'end'} pin. You can also drag existing pins to adjust their position. For precise placement, use two fingers to zoom in first.
        </InstructionsPanel>
      );
    } else if (isMobile) {
      return (
        <InstructionsPanel>
          <strong>Mobile Instructions:</strong> Press and hold on the map to place the {locationType === 'start' ? 'start' : 'end'} pin. You can also drag existing pins to adjust their position.
        </InstructionsPanel>
      );
    } else {
      return (
        <InstructionsPanel>
          <strong>Desktop Instructions:</strong> Right-click on the map to place start, end, or route stops. Drag pins to adjust. Route stops force the path through a specific road when alternate routes are not correct. <br></br>
          <strong>Mobile Instructions:</strong> Press and hold on the map to place the {locationType === 'start' ? 'start' : 'end'} pin. You can also drag existing pins to adjust their position.
        </InstructionsPanel>
      );
    }
  };

  return (
    <MapContainer>
      {!hideInstructions && renderInstructions()}
      <MapWrapper $height={height}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          options={mapOptions}
          onClick={handleMapClick}
          onRightClick={handleMapRightClick}
          onLoad={handleMapLoad}
          onIdle={handleMapIdle}
        >
          {startLocation && (
            <Marker
              position={startLocation}
              draggable={!readOnly}
              onDragEnd={(e) => handleMarkerDragEnd(e, 'start')}
              icon={createMarkerIcon('start')}
              opacity={isPointActivity ? 1 : (locationType === 'start' ? 1 : 0.3)}
            />
          )}
          {!isPointActivity && endLocation && (
            <Marker
              position={endLocation}
              draggable={!readOnly}
              onDragEnd={(e) => handleMarkerDragEnd(e, 'end')}
              icon={createMarkerIcon('end')}
              opacity={locationType === 'end' ? 1 : 0.3}
            />
          )}
          {!isPointActivity && !readOnly && routeStops.map((stop, index) => (
            <Marker
              key={stop.id}
              position={{ lat: stop.lat, lng: stop.lng }}
              draggable
              onDragEnd={(e) => handleStopDragEnd(stop.id, e)}
              icon={createStopMarkerIcon()}
              label={{
                text: String(index + 1),
                color: '#ffffff',
                fontSize: '11px',
                fontWeight: '600'
              }}
            />
          ))}
          {!isPointActivity && directionsResult && (
            <DirectionsRenderer
              directions={directionsResult}
              routeIndex={selectedRouteIndex}
              options={{
                preserveViewport: true, // Don't change map center/zoom when route updates
                suppressMarkers: true, // We use our own custom markers
                polylineOptions: {
                  strokeColor: '#0d47a1',
                  strokeOpacity: 1.0,
                  strokeWeight: 4
                }
              }}
            />
          )}
          {showInfoWindow && infoWindowPosition && (
            <InfoWindow
              position={infoWindowPosition}
              onCloseClick={() => setShowInfoWindow(false)}
            >
              <div style={{ padding: '8px' }}>
                {isPointActivity ? (
                  <div
                    style={{
                      cursor: 'pointer',
                      padding: '8px 12px',
                      fontSize: '14px'
                    }}
                    onClick={() => handleContextMenuSelect('start')}
                  >
                    <i className="bi bi-geo-alt-fill text-success me-2"></i>
                    Set Location
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        cursor: 'pointer',
                        padding: '8px 12px',
                        fontSize: '14px',
                        borderBottom: '1px solid #dee2e6'
                      }}
                      onClick={() => handleContextMenuSelect('start')}
                    >
                      <i className="bi bi-geo-alt-fill text-success me-2"></i>
                      Place Start Marker
                    </div>
                    <div
                      style={{
                        cursor: 'pointer',
                        padding: '8px 12px',
                        fontSize: '14px'
                      }}
                      onClick={() => handleContextMenuSelect('end')}
                    >
                      <i className="bi bi-geo-alt-fill text-danger me-2"></i>
                      Place End Marker
                    </div>
                    <div
                      style={{
                        cursor: routeStops.length >= MAX_ROUTE_STOPS ? 'not-allowed' : 'pointer',
                        padding: '8px 12px',
                        fontSize: '14px',
                        opacity: routeStops.length >= MAX_ROUTE_STOPS ? 0.6 : 1
                      }}
                      onClick={() => handleContextMenuSelect('stop')}
                    >
                      <i className="bi bi-signpost-2-fill text-primary me-2"></i>
                      Add Route Stop
                      {routeStops.length >= MAX_ROUTE_STOPS ? ` (max ${MAX_ROUTE_STOPS})` : ''}
                    </div>
                  </>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
        {!readOnly && (
          <>
            {!isPointActivity && (
              <AddStopButton
                type="button"
                $active={addStopMode}
                onClick={() => {
                  if (routeStops.length >= MAX_ROUTE_STOPS) {
                    alert(`Maximum of ${MAX_ROUTE_STOPS} route stops allowed.`);
                    return;
                  }
                  setAddStopMode(prev => !prev);
                }}
                disabled={routeStops.length >= MAX_ROUTE_STOPS}
                title={addStopMode ? 'Click the map to place a route stop' : 'Add a route stop to shape the path'}
              >
                <i className="bi bi-signpost-2-fill"></i>
                {addStopMode ? 'Click map to add stop' : 'Add Route Stop'}
              </AddStopButton>
            )}
            <LocationButton 
              type="button"
              onClick={() => {
                if (isPointActivity) {
                  handleGetCurrentLocation();
                } else {
                  setShowLocationDropdown(!showLocationDropdown);
                }
              }} 
              disabled={isGettingLocation}
            >
              {isGettingLocation ? (
                <>
                  <i className="bi bi-hourglass-split"></i>
                  Getting Location...
                </>
              ) : (
                <>
                  <i className="bi bi-geo-alt-fill"></i>
                  Take Current Location
                </>
              )}
            </LocationButton>
            
            {!isPointActivity && showLocationDropdown && !isGettingLocation && (
              <LocationDropdown data-location-dropdown>
                <LocationOption 
                  type="button"
                  onClick={() => handleGetCurrentLocation('start')}
                  disabled={isGettingLocation}
                >
                  <i className="bi bi-geo-alt-fill text-success"></i>
                  Set as Start Location
                </LocationOption>
                <LocationOption 
                  type="button"
                  onClick={() => handleGetCurrentLocation('end')}
                  disabled={isGettingLocation}
                >
                  <i className="bi bi-geo-alt-fill text-danger"></i>
                  Set as End Location
                </LocationOption>
              </LocationDropdown>
            )}
          </>
        )}
        <MapTypeButtons>
          <MapTypeButton
            type="button"
            active={mapType === 'roadmap'}
            onClick={() => handleMapTypeChange('roadmap')}
          >
            Map
          </MapTypeButton>
          <MapTypeButton
            type="button"
            active={mapType === 'hybrid'}
            onClick={() => handleMapTypeChange('hybrid')}
          >
            Satellite
          </MapTypeButton>
        </MapTypeButtons>
      </MapWrapper>
      {!isPointActivity && routeStops.length > 0 && (
        <StopList>
          <StopListHeader>
            <i className="bi bi-signpost-2 me-2"></i>
            Route stops — route passes through these points
          </StopListHeader>
          {routeStops.map((stop, index) => (
            <StopRow key={stop.id}>
              <RouteNumber $selected>{index + 1}</RouteNumber>
              <span>
                {stop.lat.toFixed(5)}, {stop.lng.toFixed(5)}
              </span>
              {!readOnly && (
                <StopRemoveButton type="button" onClick={() => handleRemoveStop(stop.id)}>
                  Remove
                </StopRemoveButton>
              )}
            </StopRow>
          ))}
        </StopList>
      )}
      {!isPointActivity && directionsResult && directionsResult.routes.length > 1 && routeStops.length === 0 && (
        <RouteSelector>
          <RouteSelectorHeader>
            <i className="bi bi-signpost-split me-2"></i>
            {directionsResult.routes.length} routes available — select one
          </RouteSelectorHeader>
          {directionsResult.routes.map((route, index) => {
            const totals = getRouteTotals(route);
            const distKm = totals?.distanceKm ?? null;
            const durMin = totals?.durationMin ?? null;
            const isSelected = selectedRouteIndex === index;
            return (
              <RouteOption
                key={index}
                type="button"
                $selected={isSelected}
                onMouseDown={(e) => {
                  // Keep mousedown from reaching the map / document handlers so rows stay clickable
                  e.stopPropagation();
                }}
                onClick={() => {
                  setSelectedRouteIndex(index);
                  if (onRouteSelect && distKm != null && durMin != null) {
                    onRouteSelect(distKm, durMin);
                  }
                }}
              >
                <RouteNumber $selected={isSelected}>{index + 1}</RouteNumber>
                <RouteSummaryText>{route.summary || `Route ${index + 1}`}</RouteSummaryText>
                <RouteStats>
                  {durMin != null ? `${durMin} min` : ''}
                  {distKm != null ? ` · ${distKm} km` : ''}
                </RouteStats>
              </RouteOption>
            );
          })}
        </RouteSelector>
      )}
    </MapContainer>
  );
};

export default GoogleGPSMap; 